import {
  GeneralDocumentAccess,
  DocumentContentsAccess,
  DocumentAccess,
} from "./documents/documents-permissions-resources";
import {
  LegalEntitiesPlatformAccess,
  EntityAgreementAccess,
  LegalEntityAccess,
} from "./legal-entities/legal-entities-permissions-resources";
import { CommunicationsAccess, ForumAccess } from "./messaging/messaging-permissions-resources";
import { MarketplaceAccess, MarketplaceDirectoryAccess, MarketplaceSearchAccess } from "./marketplace/marketplace-permissions-resources";
import { NotificationsAccess } from "./notifications/notifications-permissions-resources";
import { LoginAccess, UserNetworkingAccess } from "./users/users-permissions-resources";
import {
  WorkAgreementsAccess,
  ProposalsAccess,
  TeamsAccess,
  ContractsAccess,
  FeeSchedulesAccess,
  CurrencyFieldsAccess,
  DiscountsAccess,
  InvoicesAccess,
  ProposalAccess,
  WorkAgreementAccess,
  FeeScheduleTemplateAccess,
  TeamTemplateAccess,
} from "./work/work-permissions-resources";
import { KnowledgeBaseAccess } from "./knowledge-base/knowledge-base-permissions-resources";
import { AnyResource, AnyResourceJSON, ResourceFromJSON, ResourceType } from "permissions/resources/permission-resource-types";
import { LegalEntitiesPlatformAccessJSON } from "permissions/resources/legal-entities/_schemas/legal-entities-platform-access-schema";
import { LegalEntityAccessJSON } from "permissions/resources/legal-entities/_schemas/legal-entity-access-schema";
import { LoginAccessJSON } from "permissions/resources/users/_schemas/login-access-schema";
import { CalendarEventAccess } from "permissions/resources/calendar/calendar-permissions-resources";

// NOTE: This utility function is a replacement for a Resource.fromJSON static method that would
// cause a circular dependency issue if placed within the Resource class file.
export function resourceFromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
  const loaders: Record<ResourceType, (json: AnyResourceJSON) => AnyResource> = {
    [ResourceType.CalendarEventAccess]: (json) => CalendarEventAccess.fromJSON(json),

    [ResourceType.GeneralDocumentAccess]: (json) => GeneralDocumentAccess.fromJSON(json),

    [ResourceType.DocumentContentsAccess]: (json) => DocumentContentsAccess.fromJSON(json),

    [ResourceType.DocumentAccess]: (json) => DocumentAccess.fromJSON(json),

    [ResourceType.LegalEntitiesPlatformAccess]: (json) =>
      LegalEntitiesPlatformAccess.fromJSON(json as LegalEntitiesPlatformAccessJSON),

    [ResourceType.EntityAgreementAccess]: (json) => EntityAgreementAccess.fromJSON(json),

    [ResourceType.LegalEntityAccess]: (json) => LegalEntityAccess.fromJSON(json as LegalEntityAccessJSON),

    [ResourceType.CommunicationsAccess]: (json) => CommunicationsAccess.fromJSON(json),

    [ResourceType.ForumAccess]: (json) => ForumAccess.fromJSON(json),

    [ResourceType.MarketplaceAccess]: (json) => MarketplaceAccess.fromJSON(json),

    [ResourceType.MarketplaceSearchAccess]: (json) => MarketplaceSearchAccess.fromJSON(json),
    [ResourceType.MarketplaceDirectoryAccess]: (json) => MarketplaceDirectoryAccess.fromJSON(json),

    [ResourceType.NotificationsAccess]: (json) => NotificationsAccess.fromJSON(json),

    [ResourceType.LoginAccess]: (json) => LoginAccess.fromJSON(json as LoginAccessJSON),

    [ResourceType.UserNetworkingAccess]: (json) => UserNetworkingAccess.fromJSON(json),

    [ResourceType.WorkAgreementsAccess]: (json) => WorkAgreementsAccess.fromJSON(json),

    [ResourceType.ProposalsAccess]: (json) => ProposalsAccess.fromJSON(json),

    [ResourceType.TeamsAccess]: (json) => TeamsAccess.fromJSON(json),

    [ResourceType.TeamTemplateAccess]: (json) => TeamTemplateAccess.fromJSON(json),

    [ResourceType.ContractsAccess]: (json) => ContractsAccess.fromJSON(json),

    [ResourceType.FeeSchedulesAccess]: (json) => FeeSchedulesAccess.fromJSON(json),

    [ResourceType.FeeScheduleTemplateAccess]: (json) => FeeScheduleTemplateAccess.fromJSON(json),

    [ResourceType.CurrencyFieldsAccess]: (json) => CurrencyFieldsAccess.fromJSON(json),

    [ResourceType.DiscountsAccess]: (json) => DiscountsAccess.fromJSON(json),

    [ResourceType.InvoicesAccess]: (json) => InvoicesAccess.fromJSON(json),

    [ResourceType.ProposalAccess]: (json) => ProposalAccess.fromJSON(json),

    [ResourceType.WorkAgreementAccess]: (json) => WorkAgreementAccess.fromJSON(json),

    [ResourceType.KnowledgeBaseAccess]: (json) => KnowledgeBaseAccess.fromJSON(json),
  };

  const loader = loaders[json.resourceType];
  return loader(json) as ResourceFromJSON<J>;
}
